import { createSelector } from 'reselect'
import PageMetaData from '@v4/utils/data/PageMetaData'
import BaseSelector, { hookFromSelector, withStaticSelector } from './BaseSelector'
import ScorecardSelector from './ScorecardSelector'

class ThemeSelector extends BaseSelector {
	static fromStoreState(store) {
		return new this(store.theme)
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectTemplate
	 * @property {function(): Object|undefined} useTemplate
	 */
	@hookFromSelector('useTemplate')
	@withStaticSelector
	selectTemplate() {
		return this.state?.template
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectData
	 * @property {function(): Object|undefined} useData
	 */
	@hookFromSelector('useData')
	@withStaticSelector
	selectData() {
		return this.state?.data
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectThemeSettingsData
	 * @property {function(): Object|undefined} useThemeSettingsData
	 */
	@hookFromSelector('useThemeSettingsData')
	@withStaticSelector
	selectThemeSettingsData() {
		return this.selectData()?.theme_settings
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectTemplateSettings
	 * @property {function(): Object|undefined} useTemplateSettings
	 */
	@hookFromSelector('useTemplateSettings')
	@withStaticSelector
	selectTemplateSettings() {
		return this.selectTemplate()?.theme_settings
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectScorecardData
	 * @property {function(): Object|undefined} useScorecardData
	 */
	@hookFromSelector('useScorecardData')
	@withStaticSelector
	selectScorecardData() {
		return this.state?.scorecardData
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectGallery
	 * @property {function(): Object|undefined} useGallery
	 */
	@hookFromSelector('useGallery')
	@withStaticSelector
	selectGallery() {
		return this.state?.gallery
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): boolean} selectIsGalleryActive
	 * @property {function(): boolean} useIsGalleryActive
	 */
	@hookFromSelector('useIsGalleryActive')
	@withStaticSelector
	selectIsGalleryActive() {
		return !!this.selectGallery()?.active
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectSectionTemplateFields
	 * @property {function(): Object|undefined} useSectionTemplateFields
	 */
	@hookFromSelector('useSectionTemplateFields')
	@withStaticSelector
	selectSectionTemplateFields(sectionKey) {
		if (!sectionKey) {
			return undefined
		}
		const [group, section] = sectionKey.split('/')
		const template = this.selectTemplate()

		return _.get(template, ['section_groups', group, 'sections', section, 'fieldgroups', 'fields'])
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectSectionField
	 * @property {function(): Object|undefined} useSectionField
	 */
	@hookFromSelector('useSectionField')
	@withStaticSelector
	selectSectionField(fieldName, sectionKey) {
		const fields = this.selectSectionTemplateFields(sectionKey)

		return fields?.[fieldName]
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectSection
	 * @property {function(): Object|undefined} useSection
	 */
	@hookFromSelector('useSection')
	@withStaticSelector
	selectSection(sectionKey) {
		if (!sectionKey) {
			return undefined
		}
		const data = this.selectData()

		return _.get(data, ['sections', sectionKey])
	}

	/**
	 * @class ThemeSelector
	 * @property {function(storeState: Object): Object|undefined} selectSectionMeta
	 * @property {function(): Object|undefined} useSectionMeta
	 */
	@hookFromSelector('useSectionMeta')
	@withStaticSelector
	selectSectionMeta(fieldName, sectionKey) {
		return _.get(this.selectSection(sectionKey), ['meta', fieldName])
	}
}

ThemeSelector.addSelector(
	'selectExtendedScorecardData',
	createSelector(
		[ScorecardSelector.selectScorecard, ThemeSelector.selectScorecardData],
		(scorecardData, themeScorecardData) => {
			return _.merge({}, scorecardData, themeScorecardData)
		},
		{
			memoizeOptions: {
				resultEqualityCheck: _.isEqual,
			},
		},
	),
)

ThemeSelector.addSelector(
	'selectTheme',
	createSelector(
		[
			ThemeSelector.selectTemplateSettings,
			ThemeSelector.selectExtendedScorecardData,
			ThemeSelector.selectThemeSettingsData,
		],
		(templateSettings, scorecardData, themeData) => {
			return _.reduce(
				templateSettings,
				(acc, group, groupName) => {
					_.forEach(group.fields, (field, fieldName) => {
						const { relation, ...config } = field

						let value
						switch (relation?.type) {
							case 'scorecard': {
								value = _.get(scorecardData, relation.path)
								break
							}
							default: {
								const meta = themeData?.[_.join([groupName, fieldName], '.')]

								const metaData = new PageMetaData(meta, config, scorecardData)

								if (metaData.isBoolean) {
									value = metaData.toBoolean()
									break
								}

								value = metaData.isAssetable ? metaData.asset : metaData.metaValue
								break
							}
						}

						if (value !== undefined) {
							_.set(acc, [groupName, fieldName], value)
						}
					})
					return acc
				},
				{},
			)
		},
		{
			memoizeOptions: {
				resultEqualityCheck: _.isEqual,
			},
		},
	),
)

ThemeSelector.addSelector(
	'selectTypography',
	createSelector(
		[ThemeSelector.selectTheme, ThemeSelector.selectTemplateSettings, ThemeSelector.selectExtendedScorecardData],
		(theme, template, scorecardData) => {
			const getValue = (name, autoScale = false, acc = {}) => {
				let parsedValue = {}

				if (!autoScale) {
					try {
						const themeValue = _.get(theme, ['typography', name])
						parsedValue = JSON.parse(themeValue || '{}')
						// eslint-disable-next-line no-empty
					} catch (e) {}
				}

				const config = _.get(template, ['typography', 'fields', name])

				const configDefault = config?.default
				if (!configDefault) {
					return parsedValue
				}

				const defaultValue = _.reduce(
					configDefault,
					(accValue, value, key) => {
						if (typeof value === 'object' && value.fallback) {
							// eslint-disable-next-line no-param-reassign
							accValue[key] = _.get(scorecardData, value.fallback)
						} else {
							// eslint-disable-next-line no-param-reassign
							accValue[key] = value
						}

						return accValue
					},
					{},
				)

				const configInherit = config?.inherit

				const [, inheritField] = _.split(configInherit?.field, '.')

				if (!autoScale || !inheritField || !acc[inheritField]) {
					return {
						...defaultValue,
						...parsedValue,
					}
				}

				const { fontSize, ...inheritRest } = acc[inheritField] || {}
				const { fontFamily, ...defaultRest } = defaultValue

				return {
					fontFamily,
					...inheritRest,
					fontSize: Math.max(Math.round(fontSize * (configInherit.scale || 1)), 1),
					...defaultRest,
				}
			}
			const customHeadings = _.get(theme, ['typography', 'customHeadings'])

			return _.reduce(
				['h2', 'h3', 'h4', 'h5', 'h6'],
				(acc, name) => {
					acc[name] = getValue(name, !customHeadings, acc)
					return acc
				},
				{
					h1: getValue('h1'),
					body: getValue('body'),
				},
			)
		},
		{
			memoizeOptions: {
				resultEqualityCheck: _.isEqual,
			},
		},
	),
)

export default ThemeSelector
